/**
 * Compliance reports — Zod schemas.
 *
 * Reports are server-generated exports: the operator asks for a
 * report (type + date range), the backend queues a job, and the
 * UI polls for completion and surfaces a download link.
 *
 * Everything here is read-only from the UI's perspective except
 * the "request a new export" mutation.
 */

import { z } from "zod";
import { REPORT_KINDS } from "@pureid/ui";

// THE CLOSED SET COMES FROM ONE PLACE IN THE FRONTEND.
//
// It used to be written out here and again, partially, in the portal's
// auditor seat. Both apps now read `REPORT_KINDS` from the shared
// package, so a kind that is added and not named fails the build rather
// than reaching an auditor as a raw identifier.
export const reportKindSchema = z.enum(REPORT_KINDS);
export type ReportKind = z.infer<typeof reportKindSchema>;

/** Kinds whose run is a page to read, not only a file to download. */
export const DOCUMENT_REPORT_KINDS: ReadonlySet<ReportKind> = new Set<ReportKind>([
  "auth_mandate_readiness",
]);

// xlsx is a single-sheet workbook whose cells are all inline strings, so
// Excel cannot reinterpret a control id as a date or a digit-leading hash
// as scientific notation. CSV stays the default: it streams, and it is the
// format that always works.
// A counter-signature names the exported bytes, not the report row: a
// signature over a row means "I reviewed whatever that row points at now",
// while a signature over a hash is something anyone holding the file can
// check with sha256sum.
export const reportCounterSignatureFields = {
  content_sha256: z.string().optional().default(""),
  counter_signed_by_email: z.string().nullable().optional(),
  counter_signed_at: z.string().nullable().optional(),
};

export const reportFormatSchema = z.enum(["csv", "xlsx", "jsonl", "pdf"]);
export type ReportFormat = z.infer<typeof reportFormatSchema>;

export const reportStatusSchema = z.enum([
  "queued",
  "running",
  "ready",
  "failed",
  "expired",
]);
export type ReportStatus = z.infer<typeof reportStatusSchema>;

export const reportSchema = z.object({
  id: z.string(),
  kind: reportKindSchema,
  format: reportFormatSchema,
  status: reportStatusSchema,
  requested_at: z.string(),
  completed_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  row_count: z.number().int().nonnegative().nullable(),
  size_bytes: z.number().int().nonnegative().nullable(),
  error: z.string().nullable(),
  from_date: z.string(),
  to_date: z.string(),
  download_url: z.string().nullable(),
  requested_by_email: z.string().nullable(),
  // The run's kind-specific parameters, printed on the run record beside
  // the period so a reader can reproduce it. Empty for a listing kind.
  params: z.record(z.string(), z.unknown()).default({}),
  ...reportCounterSignatureFields,
});
export type Report = z.infer<typeof reportSchema>;

export const reportsListResponseSchema = z.object({
  data: z.array(reportSchema),
});
export type ReportsListResponse = z.infer<typeof reportsListResponseSchema>;

export const requestReportSchema = z.object({
  kind: reportKindSchema,
  format: reportFormatSchema,
  from_date: z.string().min(1),
  to_date: z.string().min(1),
  params: z.record(z.string(), z.unknown()).optional(),
});
export type RequestReportInput = z.infer<typeof requestReportSchema>;

// ── The three axes ───────────────────────────────────────────────────────
//
// A control carries three independent facts, and this file keeps them in
// three fields because collapsing them into one word is what made the old
// surface unreadable:
//
//   how we know — Measured · By design · Attested · Not checked yet
//   result      — Met · Partly · Not met
//   treatment   — none · Exception · Not applicable
//
// A treatment is not a result. An exception does not make a failing
// control pass; it records that someone accepted the failure for a stated
// period with a stated compensating measure. Render them separately, and
// never derive one from another.
//
// `complianceStatusSchema` used to sit here — a second, incompatible
// verdict enum ("partially_compliant" rather than "partial") with no
// remaining reader. Two enums for one domain concept is how a surface ends
// up rendering the same control two ways; it is gone.

export const dashboardStatusSchema = z.enum([
  "compliant",
  "partial",
  "non_compliant",
  "not_applicable",
  "not_assessed",
]);
export type DashboardStatus = z.infer<typeof dashboardStatusSchema>;

// How we know. "structural" renders as "By design" — a property inherent
// in how the platform is built, read from its configuration rather than
// from tenant state. The empty string is a control nobody has evaluated:
// the server used to send "auto" for those, so an unchecked control
// claimed it had been measured.
export const dashboardConfidenceSchema = z.enum([
  "auto",
  "structural",
  "manual",
  "override",
  "",
]);
export type DashboardConfidence = z.infer<typeof dashboardConfidenceSchema>;

// What the operator decided about a control, independent of the verdict.
// Absent means nothing has been decided.
export const dashboardTreatmentSchema = z.enum([
  "none",
  "exception",
  "not_applicable",
]);
export type DashboardTreatment = z.infer<typeof dashboardTreatmentSchema>;

// The one number, and the counts that make it readable. A bare index is
// never rendered on its own: "87" says nothing without "of what, checked
// how, out of how many".
// The other two axes in aggregate. A reader weighing the headline needs
// these: "87, and 40 of those are design assertions nobody has exercised"
// is a different picture from "87, all measured this morning", and the
// index alone cannot tell them apart.
export const confidenceCountsSchema = z.object({
  measured: z.number().int().nonnegative(),
  by_design: z.number().int().nonnegative(),
  attested: z.number().int().nonnegative(),
  operator_decision: z.number().int().nonnegative(),
  not_checked: z.number().int().nonnegative(),
});
export type ConfidenceCounts = z.infer<typeof confidenceCountsSchema>;

export const treatmentCountsSchema = z.object({
  exception: z.number().int().nonnegative(),
  not_applicable: z.number().int().nonnegative(),
});
export type TreatmentCounts = z.infer<typeof treatmentCountsSchema>;

export const coverageIndexSchema = z.object({
  index: z.number().int().min(0).max(100),
  assessed: z.number().int().nonnegative(),
  applicable: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export type CoverageIndex = z.infer<typeof coverageIndexSchema>;

export const dashboardCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  // Compliant by design, with no synthetic test exercising it in the
  // period. Zod strips unknown keys, so omitting this did not just hide
  // the number — it made ~45% of a register leave the Compliant tile and
  // arrive nowhere, with the screen offering no account of where they
  // went. The tile is the surface an operator explains to an auditor.
  design_only: z.number().int().nonnegative().optional().default(0),
  // ATTESTED-AND-MET, which Zod was silently stripping.
  //
  // The backend broke this out of `compliant` so the two would stop reading
  // alike, and this schema never followed — z.object drops unknown keys, so
  // the count arrived and vanished, exactly as design_only once did (see the
  // comment above it, which is the same story). The tile row then claimed to
  // reconcile to Total and could not.
  attested_met: z.number().int().nonnegative().optional().default(0),
  compliant: z.number().int().nonnegative(),
  partial: z.number().int().nonnegative(),
  non_compliant: z.number().int().nonnegative(),
  not_applicable: z.number().int().nonnegative(),
  not_assessed: z.number().int().nonnegative(),
  findings: z.number().int().nonnegative(),
  // Findings minus the failures held open by an exception that has not run
  // out — the number the "needs attention" surfaces ask for.
  //
  // The landing counted raw non_compliant + partial, which are
  // exception-blind, while the framework page derived its list from
  // attentionReasons() whose rule is "an excused failure is a decision,
  // unless the excuse has run out". So the same tenant read "5 controls are
  // not met" on the landing and found four rows on the page it linked to.
  // The rule now lives once, in Go, where the rows are.
  findings_needing_attention: z.number().int().nonnegative().optional().default(0),
});
export type DashboardCounts = z.infer<typeof dashboardCountsSchema>;

// Health is bucketed by the server (good/warning/critical) so the
// frontend doesn't re-derive it per render. `.catch` keeps a future
// new bucket from breaking the page.
export const dashboardHealthSchema = z
  .enum(["good", "warning", "critical"])
  .catch("warning");
export type DashboardHealth = z.infer<typeof dashboardHealthSchema>;

// dashboardFrameworkSchema is one card on the framework grid.
// recent_trend / trend_delta drive the mini sparkline + arrow on
// each card; older deployments may not emit these fields, so
// they default to empty / 0.
export const dashboardFrameworkSchema = z.object({
  framework_id: z.string(),
  framework_version: z.string(),
  name: z.string(),
  counts: dashboardCountsSchema,
  coverage_index: coverageIndexSchema,
  health: dashboardHealthSchema,
  recent_trend: z.array(z.number().int().min(0).max(100)).optional().default([]),
  trend_delta: z.number().int().optional().default(0),
});
export type DashboardFramework = z.infer<typeof dashboardFrameworkSchema>;

export const dashboardTopIssueSchema = z.object({
  catalog_id: z.string().uuid(),
  framework_id: z.string(),
  framework_version: z.string(),
  control_id: z.string(),
  title: z.string(),
  family: z.string(),
  severity: z.enum(["critical", "high", "medium", "low"]),
  status: dashboardStatusSchema,
  summary: z.string(),
  remediation: z.string().optional(),
});
export type DashboardTopIssue = z.infer<typeof dashboardTopIssueSchema>;

// ── Findings datatable (paginated) ────────────────────────────
//
// Backend: GET /compliance/findings (see posture/admin_handler.go).
// Powers the findings table on the compliance dashboard with
// search, sortable column headers, and infinite scroll.

export const findingsSortSchema = z.enum([
  "severity_desc",
  "severity_asc",
  "control_asc",
  "control_desc",
  "framework_asc",
  "framework_desc",
  "status_desc",
  "status_asc",
  "title_asc",
  "title_desc",
]);
export type FindingsSort = z.infer<typeof findingsSortSchema>;

export const findingsPageSchema = z.object({
  items: z.array(dashboardTopIssueSchema),
  // Opaque pagination token. The SPA never inspects this; it
  // echoes it back as the next request's cursor parameter.
  // Server-side, the token encodes the previous tail row's
  // sort-attribute snapshot so pagination stays stable even
  // when the dataset shifts between page fetches (e.g., a
  // finding becomes compliant mid-scroll).
  next_cursor: z.string().optional(),
  total: z.number().int().nonnegative(),
});
export type FindingsPage = z.infer<typeof findingsPageSchema>;

export const dashboardHeatmapCellSchema = z.object({
  family: z.string(),
  framework_id: z.string(),
  dominant_status: dashboardStatusSchema,
  control_count: z.number().int().nonnegative(),
  finding_count: z.number().int().nonnegative(),
});
export type DashboardHeatmapCell = z.infer<typeof dashboardHeatmapCellSchema>;

// ActiveException is one entry in the dashboard's "Exceptions"
// tab — a control whose verdict is held open by a time-boxed
// override. Used for countdown rendering + drill-through.
export const dashboardActiveExceptionSchema = z.object({
  catalog_id: z.string().uuid(),
  framework_id: z.string(),
  framework_version: z.string(),
  control_id: z.string(),
  title: z.string(),
  severity: z.enum(["critical", "high", "medium", "low"]),
  scope_note: z.string().optional().default(""),
  compensating_control: z.string().optional().default(""),
  expires_at: z.string(),
  override_set_at: z.string(),
});
export type DashboardActiveException = z.infer<typeof dashboardActiveExceptionSchema>;

// The catalog-level crosswalk matrix is GONE, schema and all. It reported
// what fraction of one framework's controls map into another — a property
// of the catalog's mappings, identical for every tenant, rendered on a
// per-tenant dashboard where it read as a statement about that tenant's
// evidence reuse. It had been commented out of the render for some time
// while the payload was still fetched, parsed and paid for on every load.
//
// If evidence reuse is worth showing, it has to be derived against the
// controls this tenant is actually evaluating. That is a different
// computation, and it will need a different name.

// ── Evidence locker ───────────────────────────────────────────────────────
//
// Operator-uploaded artefacts (runbooks, vendor reports, screenshots)
// linked to compliance controls. Many-to-many: one artefact can support
// many controls; one control can have many artefacts.

export const evidenceArtefactSchema = z.object({
  id: z.string().uuid(),
  filename: z.string(),
  title: z.string(),
  description: z.string(),
  content_type: z.string(),
  size_bytes: z.number().int().nonnegative(),
  sha256: z.string(),
  uploaded_at: z.string(),
  uploaded_by: z.string().uuid().optional().nullable(),
  uploaded_by_email: z.string().optional().default(""),
  retention_until: z.string().optional().nullable(),
  sensitivity: z.enum(["public_safe", "auditor_only", "internal_only"]),
  // The period the artefact is evidence for, as dates; absent when unstated.
  covers_from: z.string().optional().default(""),
  covers_to: z.string().optional().default(""),
});
export type EvidenceArtefact = z.infer<typeof evidenceArtefactSchema>;

export const evidenceListResponseSchema = z.object({
  data: z.array(evidenceArtefactSchema),
});
export type EvidenceListResponse = z.infer<typeof evidenceListResponseSchema>;

// Refresh-queue status surfaced alongside the dashboard payload and
// also as a standalone /compliance/refresh-status endpoint. The UI uses
// it to render "Last assessed N ago / Refresh now / Refreshing…" without
// round-trip storms. tenant_id is NOT on the wire — it's redundant with
// DashboardSummary.tenant_id and the URL the request was made against.
export const refreshStatusSchema = z.object({
  next_run_at: z.string(),
  priority: z.string(),
  reason: z.string(),
  in_flight: z.boolean(),
  claimed_by: z.string().nullable().optional(),
  claimed_at: z.string().nullable().optional(),
  last_completed_at: z.string().nullable().optional(),
  last_failed_at: z.string().nullable().optional(),
  last_duration_ms: z.number().int().nullable().optional(),
  last_error: z.string().nullable().optional(),
  requested_by: z.string().uuid().nullable().optional(),
});
export type RefreshStatus = z.infer<typeof refreshStatusSchema>;

// One in-scope control a report kind stands behind — the report↔register
// bridge, derived server-side from the evaluator map. See the report
// catalogue: a card that cannot say which obligations its rows evidence
// sits beside the register unmapped, in both directions.
export const reportEvidenceRefSchema = z.object({
  catalog_id: z.string().uuid(),
  framework_id: z.string(),
  framework_version: z.string(),
  control_id: z.string(),
  title: z.string(),
});
export type ReportEvidenceRef = z.infer<typeof reportEvidenceRefSchema>;

const baseDashboardSummarySchema = z.object({
  tenant_id: z.string().uuid(),
  generated_at: z.string(),
  coverage_index: coverageIndexSchema,
  counts: dashboardCountsSchema,
  by_confidence: confidenceCountsSchema,
  treatments: treatmentCountsSchema,
  frameworks: z.array(dashboardFrameworkSchema),
  top_issues: z.array(dashboardTopIssueSchema),
  heatmap: z.array(dashboardHeatmapCellSchema),
  active_exceptions: z.array(dashboardActiveExceptionSchema).optional().default([]),
  // Keyed by report kind as a plain string, not reportKindSchema: a kind
  // this build does not know must be ignored, never crash the dashboard
  // — the same deploy-skew tolerance as responsibilities below.
  report_evidence: z
    .record(z.string(), z.array(reportEvidenceRefSchema))
    .optional()
    .default({}),
});

// Dashboard payload is the legacy summary plus the v2 refresh-queue
// status. refresh_status is optional only to keep older snapshots
// parseable; new deployments always emit it.
export const dashboardSummarySchema = baseDashboardSummarySchema.extend({
  refresh_status: refreshStatusSchema.optional(),
});
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;

// Who acted, at which level. "tenant" is a home admin and prints nothing
// extra; the other two are printed beside the email so a reader of the
// record can tell a delegated administrator or PureID itself from the
// tenant's own people. See actorLabel in labels.ts.
export const actorScopeSchema = z.enum(["tenant", "tenant_grant", "platform"]);
export type ActorScope = z.infer<typeof actorScopeSchema>;

// Why a pack is not offered. Present only when offered is false.
export const notOfferedReasonSchema = z.enum([
  "internal",
  "licence_unreviewed",
  "licence_blocked",
]);
export type NotOfferedReason = z.infer<typeof notOfferedReasonSchema>;

export const licenceStatusSchema = z.enum(["reviewed", "unreviewed", "blocked"]);
export type LicenceStatus = z.infer<typeof licenceStatusSchema>;

// Whether the pack's statutory citations have been checked, and by whom.
// Printed on every card and the framework page that is not counsel-verified.
export const verificationSchema = z.enum([
  "not_applicable",
  "unverified",
  "engineering_verified",
  "counsel_verified",
]);
export type Verification = z.infer<typeof verificationSchema>;

// Per-framework drill-in
export const controlListItemSchema = z.object({
  catalog_id: z.string().uuid(),
  control_id: z.string(),
  title: z.string(),
  family: z.string(),
  severity: z.enum(["critical", "high", "medium", "low"]),
  // The three axes, separate. status is what was measured; treatment is
  // what the operator decided about it. A control held open by an
  // exception is "not met, treated as an exception" — never "not
  // applicable", which is what the old single field said.
  status: dashboardStatusSchema,
  treatment: dashboardTreatmentSchema.optional().default("none"),
  confidence: dashboardConfidenceSchema,
  summary: z.string(),
  // What the control actually asks for. The register used to show only a
  // title, so an auditor comparing our reading against the instrument had
  // to open every row to see what we thought it was about.
  requirement: z.string().optional().default(""),
  // The instrument's own grouping. An auditor navigates a framework by
  // its structure, not by our control families.
  section: z.string().optional().default(""),
  supplier: z.string().optional().default(""),
  population: z.string().optional().default(""),
  customer_control_id: z.string().optional().default(""),
  // Both parties, by name and role.
  owner_name: z.string().optional().default(""),
  owner_role: z.string().optional().default(""),
  reviewer_name: z.string().optional().default(""),
  reviewer_role: z.string().optional().default(""),
  // When the standing treatment or assertion ends.
  exception_expires_at: z.string().optional().default(""),
  attestation_expires_at: z.string().optional().default(""),
  // The control the statement was made on, when this row cites it.
  attestation_cited_from: z.string().optional().default(""),
  compensating_control: z.string().optional().default(""),
  // A structural claim no synthetic test has exercised in the period.
  // The register must not draw it as "Met" — design and operating
  // effectiveness are the two halves of a service-auditor opinion.
  design_only: z.boolean().optional().default(false),
});
export type ControlListItem = z.infer<typeof controlListItemSchema>;

// One line of the shared-responsibility split. `ours` is resolved on the
// wire, so the surface renders a fact rather than interpreting an absence.
export const frameworkObligationSchema = z.object({
  title: z.string(),
  detail: z.string(),
  ours: z.boolean(),
});
export type FrameworkObligation = z.infer<typeof frameworkObligationSchema>;

export const frameworkDetailSchema = z.object({
  framework_id: z.string(),
  framework_version: z.string(),
  name: z.string(),
  description: z.string(),
  // Whether this framework is part of what the tenant answers to. The
  // register is readable before selection — the chooser links here so
  // an operator can read the obligations before taking them on — and
  // the page says which kind of register it is showing. Defaulted true
  // for deploy skew: an older backend only ever served selected
  // frameworks, so absence means in scope.
  selected: z.boolean().optional().default(true),
  // Whether the pack can be added to scope today, and the state of its
  // licence and citation review. Defaulted for the same deploy skew as
  // `selected`: an older backend served only packs it offered.
  offered: z.boolean().optional().default(true),
  licence_status: licenceStatusSchema.optional().default("reviewed"),
  verification: verificationSchema.optional().default("not_applicable"),
  counts: dashboardCountsSchema,
  coverage_index: coverageIndexSchema,
  // The other two axes, scoped to this framework. A coverage number
  // without them asks the reader to assume how much of it is measured.
  by_confidence: confidenceCountsSchema,
  treatments: treatmentCountsSchema,
  // The denominator, and why we ship this numerator. Zero when the pack
  // carries the whole instrument — the surface then says nothing rather
  // than inventing one.
  instrument_total: z.number().int().nonnegative().optional().default(0),
  subset_rationale: z.string().optional().default(""),
  responsibilities_lede: z.string().optional().default(""),
  // .nullable() is load-bearing: Zod applies a default to `undefined`
  // only, so a `null` from a pack with no responsibilities block threw in
  // the route loader and took the whole framework page down. The backend
  // now sends `[]`; this tolerates both, because a client that trusts one
  // server shape is one deploy-skew away from the same crash.
  responsibilities: z
    .array(frameworkObligationSchema)
    .nullable()
    .optional()
    .default([])
    .transform((v) => v ?? []),
  controls: z.array(controlListItemSchema),
});
export type FrameworkDetail = z.infer<typeof frameworkDetailSchema>;

// Per-control detail
export const crosswalkEntrySchema = z.object({
  framework_id: z.string(),
  framework_version: z.string(),
  control_id: z.string(),
  relationship: z.enum(["equivalent", "narrower", "broader", "related"]),
});
export type CrosswalkEntry = z.infer<typeof crosswalkEntrySchema>;

export const controlOverrideDetailSchema = z.object({
  status: z.enum(["not_applicable", "exception_temporary"]),
  scope_note: z.string(),
  override_set_by: z.string().uuid().nullable().optional(),
  // Both parties by email. The backend has denormalised these since the
  // approver hole was closed, precisely so the record survives an admin
  // being offboarded — and the schema did not read them, so the surface
  // has been showing a treatment with no author on it.
  override_set_by_email: z.string().optional().default(""),
  approved_by_email: z.string().optional().default(""),
  override_set_at: z.string().optional(),
  // Time-boxed exception fields. Present only when
  // status === "exception_temporary".
  expires_at: z.string().nullable().optional(),
  approver_id: z.string().uuid().nullable().optional(),
  compensating_control: z.string().optional().default(""),
});
export type ControlOverrideDetail = z.infer<typeof controlOverrideDetailSchema>;

export const controlDetailSchema = z.object({
  catalog_id: z.string().uuid(),
  framework_id: z.string(),
  framework_version: z.string(),
  // Same semantics and same deploy-skew default as the framework's flag:
  // an older backend only ever served in-scope controls.
  selected: z.boolean().optional().default(true),
  control_id: z.string(),
  title: z.string(),
  requirement: z.string(),
  family: z.string(),
  severity: z.enum(["critical", "high", "medium", "low"]),
  status: dashboardStatusSchema,
  treatment: dashboardTreatmentSchema.optional().default("none"),
  confidence: dashboardConfidenceSchema,
  // Evidence is an open-ended map; we accept anything but always
  // expect a `summary` string. The dashboard renders `summary`
  // prominently and the rest as typed key/value pairs.
  evidence: z.record(z.string(), z.unknown()).optional(),
  remediation: z.string().optional(),
  evaluator_version: z.string().optional(),
  assessed_at: z.string().optional(),
  crosswalk: z.array(crosswalkEntrySchema),
  override: controlOverrideDetailSchema.nullable().optional(),
  // The retained population of runs over the trailing window: what an
  // examiner means by operating effectiveness, as a count with its
  // denominator.
  executions: z
    .object({
      since: z.string(),
      runs: z.number().int().nonnegative(),
      met: z.number().int().nonnegative(),
      partly: z.number().int().nonnegative(),
      not_met: z.number().int().nonnegative(),
      other: z.number().int().nonnegative(),
      first_at: z.string().nullable().optional(),
      last_at: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  // The instrument's own framing, from the pack.
  section: z.string().optional().default(""),
  supplier: z.string().optional().default(""),
  population: z.string().optional().default(""),
  // Who answers for it, by name and by role. The owner is the owner —
  // never the person who last set a treatment on the control.
  owner_name: z.string().optional().default(""),
  owner_role: z.string().optional().default(""),
  reviewer_name: z.string().optional().default(""),
  reviewer_role: z.string().optional().default(""),
  // The ids behind those names, so the ownership form opens on the people
  // who currently hold each line. Absent on the auditor path, which is
  // shown names and never internal identifiers.
  owner_admin_id: z.string().uuid().nullable().optional(),
  reviewer_admin_id: z.string().uuid().nullable().optional(),
  customer_control_id: z.string().optional().default(""),
  // What is currently asserted or decided, and when each stops being true.
  attestation_statement: z.string().optional().default(""),
  attestation_expires_at: z.string().optional().default(""),
  // When the statement was made on another control that asks the same
  // question: that control as the register names it, and its id.
  attestation_cited_from: z.string().optional().default(""),
  attestation_cited_from_id: z.string().uuid().nullable().optional(),
  exception_expires_at: z.string().optional().default(""),
  compensating_control: z.string().optional().default(""),
  next_review_due_at: z.string().optional().default(""),
  design_only: z.boolean().optional().default(false),
  // The operational reports whose rows carry the record behind this
  // control's check. Plain strings, filtered against the catalogue at
  // render time: a kind this build does not know is ignored, never a
  // parse failure taking the control page down.
  supporting_report_kinds: z.array(z.string()).optional().default([]),
});
export type ControlDetail = z.infer<typeof controlDetailSchema>;

// setOverrideInputSchema is GONE, with the endpoint and the mutation.
//
// It was one union covering two acts that turned out to be different in
// kind: marking a control not applicable, which removes it from the
// denominator and now takes two people through a request, and granting a
// time-boxed exception, which leaves the gap counted and has always had a
// named approver. Modelling them as one shape is part of how the
// exception branch came to post to an endpoint that refuses it.

// ── Phase E: auditor portal tokens ─────────────────────────────────────

export const auditorTokenStatusSchema = z.enum(["active", "revoked"]);
export type AuditorTokenStatus = z.infer<typeof auditorTokenStatusSchema>;

// A party outside this deployment that holds copies of the transparency
// log's signed tree heads.
//
// The register answers two questions and the second is the one that
// matters: who did we ask, and have they actually been watching. A key on
// file with no recent signature is a party that was asked and has not
// answered, which is a finding rather than a blank.
export const transparencyWitnessSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  key_id: z.string(),
  registered_by_email: z.string(),
  registered_at: z.string(),
  revoked_at: z.string().nullable().optional(),
  revoked_by_email: z.string().nullable().optional(),
  last_tree_size: z.number().int().nonnegative().nullable().optional(),
  last_witnessed_at: z.string().nullable().optional(),
});
export type TransparencyWitness = z.infer<typeof transparencyWitnessSchema>;

export const transparencyWitnessListSchema = z.object({
  data: z.array(transparencyWitnessSchema).nullable().default([]),
  // The key a witness must be pointed at. Empty on a deployment with no
  // signing key, where nothing is worth witnessing yet.
  log_signing_key_id: z.string().optional().default(""),
});
export type TransparencyWitnessList = z.infer<typeof transparencyWitnessListSchema>;

// A 32-byte Ed25519 public key, as the witness printed it: 64 hex
// characters or base64. Anything else is refused rather than coerced.
export const registerWitnessSchema = z.object({
  name: z.string().trim().min(1, "Name the party that will hold the copies").max(200),
  public_key: z
    .string()
    .trim()
    .min(1, "Paste the public key the witness printed when it started")
    .refine(
      (v) => /^[0-9a-fA-F]{64}$/.test(v) || /^[A-Za-z0-9+/]{42,44}={0,2}$/.test(v),
      "That is not a 32-byte Ed25519 public key",
    ),
});
export type RegisterWitnessInput = z.infer<typeof registerWitnessSchema>;

export const auditorTokenSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  description: z.string(),
  status: auditorTokenStatusSchema,
  expires_at: z.string(),
  last_used_at: z.string().nullable().optional(),
  use_count: z.number().int().nonnegative(),
  created_by: z.string().nullable().optional(),
  // The ACCOUNTABLE human — who answers for this now, as distinct from created_by
  // (who minted it, historically). Absent when nobody is, which the non-human
  // identity census renders as a finding rather than a blank.
  owner_admin_id: z.string().uuid().nullable().optional(),
  created_at: z.string(),
  revoked_at: z.string().nullable().optional(),
  // `token` is populated ONCE on the issue response. Reads omit it.
  token: z.string().optional(),
});
export type AuditorToken = z.infer<typeof auditorTokenSchema>;

export const auditorTokenListSchema = z.object({
  data: z.array(auditorTokenSchema),
});
export type AuditorTokenList = z.infer<typeof auditorTokenListSchema>;

export const issueAuditorTokenInputSchema = z.object({
  description: z
    .string()
    .min(1, "Description is required")
    .max(256, "Description must be 256 characters or fewer"),
  // Server-side: must be RFC3339 in [now+1min, now+90d].
  expires_at: z.string(),
});
export type IssueAuditorTokenInput = z.infer<typeof issueAuditorTokenInputSchema>;

export const auditorAccessLogEntrySchema = z.object({
  id: z.string().uuid(),
  token_id: z.string().uuid().nullable().optional(),
  method: z.string(),
  path: z.string(),
  response_status: z.number().int(),
  ip_address: z.string().nullable().optional(),
  user_agent: z.string().nullable().optional(),
  created_at: z.string(),
});
export type AuditorAccessLogEntry = z.infer<typeof auditorAccessLogEntrySchema>;

export const auditorAccessLogListSchema = z.object({
  data: z.array(auditorAccessLogEntrySchema),
});
export type AuditorAccessLogList = z.infer<typeof auditorAccessLogListSchema>;

// ── Phase G: trend + what-changed feed ─────────────────────────────────

export const trendPointSchema = z.object({
  at: z.string(),
  score: z.number().int().min(0).max(100),
  counts: dashboardCountsSchema,
});
export type TrendPoint = z.infer<typeof trendPointSchema>;

export const trendSeriesSchema = z.object({
  points: z.array(trendPointSchema),
  current_score: z.number().int().min(0).max(100),
  window_start: z.string(),
  window_end: z.string(),
  score_delta: z.number().int(),
  oldest_observed: z.string().nullable().optional(),
});
export type TrendSeries = z.infer<typeof trendSeriesSchema>;

export const changedControlSchema = z.object({
  snapshot_id: z.string().uuid(),
  catalog_id: z.string().uuid().nullable().optional(),
  framework_id: z.string(),
  framework_version: z.string(),
  control_id: z.string(),
  title: z.string(),
  family: z.string(),
  severity: z.enum(["critical", "high", "medium", "low"]),
  status: dashboardStatusSchema,
  previous_status: z.string().optional(),
  captured_at: z.string(),
  captured_reason: z.enum([
    "state_change",
    "evidence_change",
    "override_change",
    "manual_review",
    "evaluator_upgrade",
  ]),
  captured_by: z.string().uuid().nullable().optional(),
  summary: z.string().optional(),
});
export type ChangedControl = z.infer<typeof changedControlSchema>;

export const changedControlsListSchema = z.object({
  data: z.array(changedControlSchema),
  since: z.string(),
});
export type ChangedControlsList = z.infer<typeof changedControlsListSchema>;

// ── Phase H: audit-readiness ───────────────────────────────────────────

export const checkStatusSchema = z.enum(["pass", "warn", "fail"]);
export type CheckStatus = z.infer<typeof checkStatusSchema>;

export const readinessCheckSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  status: checkStatusSchema,
  severity: z.enum(["blocker", "advisory"]),
  detail: z.string().optional(),
  remediation: z.string().optional(),
  affected_control_ids: z.array(z.string().uuid()).optional(),
});
export type ReadinessCheck = z.infer<typeof readinessCheckSchema>;

export const readinessReportSchema = z.object({
  framework_id: z.string(),
  framework_version: z.string(),
  name: z.string(),
  generated_at: z.string(),
  overall_status: checkStatusSchema,
  overall_narrative: z.string(),
  blocker_count: z.number().int().nonnegative(),
  advisory_count: z.number().int().nonnegative(),
  pass_count: z.number().int().nonnegative(),
  checks: z.array(readinessCheckSchema),
});
export type ReadinessReport = z.infer<typeof readinessReportSchema>;

// ── Phase I: questionnaire auto-fill ───────────────────────────────────

export const questionnaireFormatSchema = z.enum(["caiq_v4", "sig_lite"]);
export type QuestionnaireFormat = z.infer<typeof questionnaireFormatSchema>;

export const questionnaireRowSchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
  evidence: z.string().optional(),
  confidence: z.enum(["auto", "partial", "manual_input_required"]),
});
export type QuestionnaireRow = z.infer<typeof questionnaireRowSchema>;

export const questionnaireSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  questions: z.array(questionnaireRowSchema),
});
export type QuestionnaireSection = z.infer<typeof questionnaireSectionSchema>;

export const questionnaireResponseSchema = z.object({
  format: questionnaireFormatSchema,
  tenant_id: z.string().uuid(),
  generated_at: z.string(),
  questions_total: z.number().int().nonnegative(),
  questions_answered: z.number().int().nonnegative(),
  sections: z.array(questionnaireSectionSchema),
  disclaimer: z.string(),
});
export type QuestionnaireResponse = z.infer<typeof questionnaireResponseSchema>;


// ── Two-person scope exclusion ───────────────────────────────────────────
//
// Marking a control not applicable removes it from the denominator
// entirely. It used to be one person's click; it is now a request a
// colleague decides, and the single-person endpoint is gone rather than
// deprecated — leaving it would have made the two-person path optional.
//
// A rejected request is kept, with its reason: "we considered scoping this
// out and decided not to" is what an examination asks about.

export const requestKindSchema = z.enum(["scope_exclusion", "exception"]);
export type RequestKind = z.infer<typeof requestKindSchema>;

export const scopeRequestSchema = z.object({
  id: z.string(),
  // Which claim is being made. An exclusion says the control does not
  // apply and leaves the denominator; an exception says it applies, is
  // not met, and is accepted until a date with something covering the
  // gap. Both take two people; they are not the same ask.
  kind: requestKindSchema,
  compensating_control: z.string().optional().default(""),
  requested_expires_at: z.string().optional().default(""),
  // When the request itself expires unanswered. Shown to the reviewer,
  // because "you have four days" is the difference between a queue and a
  // pile.
  lapses_at: z.string(),
  catalog_id: z.string(),
  framework_id: z.string(),
  framework_version: z.string(),
  control_id: z.string(),
  control_title: z.string(),
  rationale: z.string(),
  requested_by_email: z.string(),
  requested_by_scope: actorScopeSchema.optional(),
  requested_at: z.string(),
  reviewer_email: z.string(),
  // "lapsed" is distinct from "withdrawn": withdrawn is the requester
  // changing their mind, lapsed is the organisation failing to answer.
  // An examination reads those very differently.
  status: z.enum(["pending", "approved", "rejected", "withdrawn", "lapsed"]),
  decided_at: z.string().nullable().optional(),
  decided_by_email: z.string().nullable().optional(),
  decided_by_scope: actorScopeSchema.optional(),
  decision_note: z.string().nullable().optional(),
});
export type ScopeRequest = z.infer<typeof scopeRequestSchema>;

export const scopeRequestListSchema = z.object({ data: z.array(scopeRequestSchema) });


// ── The framework chooser ────────────────────────────────────────────────
//
// Name, blurb and region come from the pack, beside the controls they
// describe — a chooser blurb that promises something the controls do not
// check is a promise made at the very moment of selection.

export const availableFrameworkSchema = z.object({
  framework_id: z.string(),
  framework_version: z.string(),
  name: z.string(),
  blurb: z.string().optional().default(""),
  jurisdiction: z.string().optional().default(""),
  region: z.string().optional().default(""),
  // What selecting it takes on. Shown so the choice is not blind.
  control_count: z.number().int().nonnegative(),
  // How each control will be known, on the same three-value ladder the
  // dashboard uses after selection. Classified through the evaluator
  // registry, never from evaluator presence — a symbol is not a check.
  measured_count: z.number().int().nonnegative().optional().default(0),
  by_design_count: z.number().int().nonnegative().optional().default(0),
  // The attested bucket, split by who does the attesting. A
  // platform-supplied attestation is PureID's to make, and putting it on
  // the buyer's side of the card is the one thing this line must not do.
  you_attest_count: z.number().int().nonnegative().optional().default(0),
  we_attest_count: z.number().int().nonnegative().optional().default(0),
  requires: z.array(z.string()).nullable().optional().default([]),
  selected: z.boolean(),
  // Who put it in scope, and when. Absent when it is not selected.
  selected_by_email: z.string().optional(),
  selected_by_scope: actorScopeSchema.optional(),
  selected_at: z.string().optional(),
  // Whether it can be added today. The server omits packs that are neither
  // offered nor selected; a selected pack that is no longer offered arrives
  // with offered=false so it can still be removed.
  offered: z.boolean(),
  not_offered_reason: notOfferedReasonSchema.optional(),
  licence_status: licenceStatusSchema,
  verification: verificationSchema,
});
export type AvailableFramework = z.infer<typeof availableFrameworkSchema>;

export const frameworkSelectionSchema = z.object({
  selected: z.array(
    z.object({
      framework_id: z.string(),
      framework_version: z.string(),
      selected_by_email: z.string().optional().default(""),
      selected_by_scope: actorScopeSchema.optional(),
      selected_at: z.string(),
    }),
  ),
  available: z.array(availableFrameworkSchema),
});
export type FrameworkSelection = z.infer<typeof frameworkSelectionSchema>;

// ── One control's own record ──────────────────────────────────────────
//
// Every snapshot of this control, newest first, each with the chain hash
// it was sealed with. The hash is what makes this a record rather than a
// log: it is the same hash the signed packet carries, and anyone holding
// the packet can check it.
export const controlHistoryEntrySchema = z.object({
  snapshot_id: z.string().uuid(),
  status: dashboardStatusSchema,
  previous_status: z.string().optional().default(""),
  captured_at: z.string(),
  captured_reason: z.enum([
    "state_change",
    "evidence_change",
    "override_change",
    "manual_review",
    "evaluator_upgrade",
  ]),
  captured_by_email: z.string().optional().default(""),
  evaluator_version: z.string().optional().default(""),
  summary: z.string().optional().default(""),
  row_hash: z.string(),
});
export type ControlHistoryEntry = z.infer<typeof controlHistoryEntrySchema>;

export const controlHistoryResponseSchema = z.object({
  data: z.array(controlHistoryEntrySchema).nullable().default([]),
});

// Naming who answers for a control. Both people are optional and both
// roles are required alongside their person; an explicit null clears.
export const setOwnershipSchema = z.object({
  owner_admin_id: z.string().uuid().nullable(),
  owner_role: z.string(),
  reviewer_admin_id: z.string().uuid().nullable(),
  reviewer_role: z.string(),
});
export type SetOwnershipInput = z.infer<typeof setOwnershipSchema>;

// ── Attestation ───────────────────────────────────────────────────────
//
// A person's statement that a control holds, standing until an expiry,
// reviewed by a second person, and resting on at least one document. All
// four are required by the server; the form enforces the same four so the
// reason is visible before the request rather than in a toast after it.
export const MIN_ATTESTATION_STATEMENT = 20;
export const MAX_ATTESTATION_MONTHS = 12;

export const attestControlSchema = z.object({
  statement: z.string().min(MIN_ATTESTATION_STATEMENT),
  expires_at: z.string().min(1),
  reviewer_admin_id: z.string().uuid(),
  artefact_ids: z.array(z.string().uuid()).min(1),
});
export type AttestControlInput = z.infer<typeof attestControlSchema>;

// What the counter-signature endpoint returns: the reviewer, the instant,
// and the bytes they signed over. The hash is echoed back because a
// signature over a row would mean "I reviewed whatever that row points at
// now" — over a hash it is something anyone holding the file can check.
export const counterSignatureSchema = z.object({
  counter_signed_by_email: z.string(),
  counter_signed_at: z.string(),
  content_sha256: z.string(),
});
export type CounterSignature = z.infer<typeof counterSignatureSchema>;

// ── Attestations waiting on a second person ──────────────────────────
//
// An attestation is offered and only becomes evidence when a colleague
// accepts it from their own session. Until then the control reads as it
// did — so this list is the only place the pending state is visible, and
// without it an offered attestation would vanish until somebody guessed
// to look.
export const attestationRequestSchema = z.object({
  id: z.string().uuid(),
  catalog_id: z.string().uuid(),
  framework_id: z.string(),
  framework_version: z.string(),
  control_id: z.string(),
  control_title: z.string(),
  statement: z.string(),
  expires_at: z.string(),
  artefact_count: z.number().int().nonnegative(),
  // The documents themselves, by name and content hash. Hash pinning
  // guarantees "the same bytes as at the offer"; it cannot guarantee "the
  // bytes the reviewer read" if the reviewer is only ever told there are
  // three of them.
  artefacts: z
    .array(
      z.object({
        id: z.string().uuid(),
        filename: z.string(),
        sha256: z.string(),
      }),
    )
    .nullable()
    .default([]),
  attested_by_email: z.string(),
  attested_by_scope: actorScopeSchema.optional(),
  attested_at: z.string(),
  reviewer_email: z.string(),
  status: z.enum(["pending", "approved", "rejected", "lapsed"]),
  decided_by_email: z.string().optional().default(""),
  decided_by_scope: actorScopeSchema.optional(),
  decided_at: z.string().optional().default(""),
  decision_note: z.string().optional().default(""),
});
export type AttestationRequest = z.infer<typeof attestationRequestSchema>;

export const attestationRequestListSchema = z.object({
  data: z.array(attestationRequestSchema).nullable().default([]),
});

// ── Period closes waiting on a second person ─────────────────────────
//
// The packet is frozen and hashed when the close is offered; the
// signature is applied at the decision, over that same root. The hash is
// on this row because the reviewer is approving specific bytes, and
// showing it is what makes "I reviewed this packet" checkable.
export const pendingCloseSchema = z.object({
  id: z.string().uuid(),
  period_start: z.string(),
  period_end: z.string(),
  manifest_sha256: z.string(),
  requested_by_email: z.string(),
  requested_by_scope: actorScopeSchema.optional(),
  requested_by_role: z.string(),
  requested_at: z.string(),
  reviewer_email: z.string(),
  // The answer, when there is one. A refusal that leaves the surface takes
  // its reason with it, and the reason is the closer's whole next move.
  status: z.enum(["pending", "approved", "rejected", "lapsed"]),
  decided_by_email: z.string().optional().default(""),
  decided_by_scope: actorScopeSchema.optional(),
  decided_at: z.string().optional().default(""),
  decision_note: z.string().optional().default(""),
});
export type PendingClose = z.infer<typeof pendingCloseSchema>;

export const pendingCloseListSchema = z.object({
  data: z.array(pendingCloseSchema).nullable().default([]),
});

// ── The closes that were signed ──────────────────────────────────────
//
// Append-only by trigger: a close is a statement two people put their
// names to, and re-closing writes a new row rather than editing one. The
// signature is over the manifest root, which anyone holding the packet
// re-derives without asking us — which is why both are on the row.
export const periodCloseSchema = z.object({
  id: z.string().uuid(),
  period_start: z.string(),
  period_end: z.string(),
  closed_by_email: z.string(),
  closed_by_scope: actorScopeSchema.optional(),
  closed_by_role: z.string(),
  reviewed_by_email: z.string(),
  reviewed_by_scope: actorScopeSchema.optional(),
  reviewed_by_role: z.string(),
  manifest_sha256: z.string(),
  signature: z.string(),
  closed_at: z.string(),
  // Both signatures were PureID's, made on the tenant's behalf. Said
  // beside the close, because two names from one organisation is what a
  // reader assumes and this is the case where that assumption is wrong.
  provider_only: z.boolean().optional().default(false),
});
export type PeriodClose = z.infer<typeof periodCloseSchema>;

export const periodCloseListSchema = z.object({
  data: z.array(periodCloseSchema).nullable().default([]),
});

// ── Authentication-mandate readiness ─────────────────────────────────────
//
// The document behind docs/product-areas.md. The wire
// shape is the worker's own: every count carries its denominator, and a
// clause with a zero denominator carries a sentence (has_fraction false).

export const mandateMeasureSchema = z.enum([
  "privileged_phishing_resistant",
  "otp_not_accepted",
  "phishing_resistant_default",
  "control",
]);

export const mandateClauseSchema = z.object({
  id: z.string(),
  title: z.string(),
  in_instrument: z.boolean(),
  source: z.string(),
  measure: mandateMeasureSchema,
  control_evaluators: z.array(z.string()).optional(),
});

export const mandateRuleSetSchema = z.object({
  id: z.string(),
  name: z.string(),
  region: z.string(),
  instruments: z.array(z.string()),
  counsel_rows: z.array(z.number()),
  clauses: z.array(mandateClauseSchema),
});
export type MandateRuleSet = z.infer<typeof mandateRuleSetSchema>;

export const mandateRuleSetsResponseSchema = z.object({
  data: z.array(mandateRuleSetSchema),
});

export const mandateHowKnownSchema = z.enum(["measured", "attested", "by_design", "not_checked"]);
export type MandateHowKnown = z.infer<typeof mandateHowKnownSchema>;
export const mandateResultSchema = z.enum(["met", "partly", "not_met", ""]);
export type MandateResult = z.infer<typeof mandateResultSchema>;

const mandateGroupSchema = z.object({ key: z.string(), label: z.string(), count: z.number().int() });

export const mandateClauseCardSchema = z.object({
  clause: mandateClauseSchema,
  how_known: mandateHowKnownSchema,
  result: mandateResultSchema.optional().default(""),
  has_fraction: z.boolean(),
  numerator: z.number().int(),
  denominator: z.number().int(),
  unit: z.string(),
  statement: z.string(),
  detail: z.string(),
  control: z
    .object({
      catalog_id: z.string(),
      framework_id: z.string(),
      framework_version: z.string(),
      control_id: z.string(),
      title: z.string(),
    })
    .nullable()
    .optional(),
  attestation: z
    .object({
      reviewer_email: z.string(),
      statement: z.string(),
      expires_at: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type MandateClauseCard = z.infer<typeof mandateClauseCardSchema>;

export const mandateDocumentSchema = z.object({
  kind: z.literal("auth_mandate_readiness"),
  rule_set: mandateRuleSetSchema,
  period_from: z.string(),
  period_to: z.string(),
  generated_at: z.string(),
  population: z.object({
    as_of: z.string(),
    total: z.number().int(),
    groups: z.array(mandateGroupSchema),
    excluded: z.array(mandateGroupSchema),
    note: z.string(),
  }),
  policy: z.object({
    tenant_methods: z.array(z.string()),
    platform_methods: z.array(z.string()),
    passkey_required: z.boolean(),
  }),
  clauses: z.array(mandateClauseCardSchema),
  outside_rule: z.array(
    z.object({
      severity: z.enum(["warn", "bad"]),
      count: z.number().int(),
      sentence: z.string(),
      why: z.string(),
      list_key: z.string(),
    }),
  ),
  methods: z.array(
    z.object({
      method: z.string(),
      label: z.string(),
      phishing_resistant: z.boolean(),
      otp_class: z.boolean(),
      sign_ins: z.number().int(),
      people: z.number().int(),
    }),
  ),
});
export type MandateDocument = z.infer<typeof mandateDocumentSchema>;
